import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { Package, ShoppingCart, Warehouse, CheckCircle, XCircle, DollarSign, Ship, Building2, AlertTriangle, FileText, Download, Shield, Gavel, Clock } from "lucide-react";
import { format } from "date-fns";
import { getApprovalTier, formatValueRange, getApprovalChain } from "@/data/approvalChainOfCustody";

const statusColors: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  submitted: "bg-blue-500/20 text-blue-400",
  pending_approval: "bg-amber-500/20 text-amber-400",
  approved: "bg-emerald-500/20 text-emerald-400",
  rejected: "bg-destructive/20 text-destructive",
  ordered: "bg-indigo-500/20 text-indigo-400",
  delivered: "bg-teal/20 text-teal",
  completed: "bg-lime/20 text-lime",
  cancelled: "bg-muted text-muted-foreground",
};

const AdminSupplyChain = () => {
  const { user, fullName } = useAuth();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState("approvals");
  const [filterScope, setFilterScope] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [approvalNotes, setApprovalNotes] = useState<Record<string, string>>({});
  const [detailOrder, setDetailOrder] = useState<any>(null);

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ["admin-purchase-orders"],
    queryFn: async () => {
      const { data, error } = await supabase.from("purchase_orders").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: orderItems = [] } = useQuery({
    queryKey: ["admin-order-items", detailOrder?.id],
    queryFn: async () => {
      if (!detailOrder) return [];
      const { data, error } = await supabase.from("purchase_order_items").select("*").eq("order_id", detailOrder.id);
      if (error) throw error;
      return data;
    },
    enabled: !!detailOrder,
  });

  const { data: inventory = [] } = useQuery({
    queryKey: ["admin-inventory"],
    queryFn: async () => {
      const { data, error } = await supabase.from("inventory_items").select("*").order("name");
      if (error) throw error;
      return data;
    },
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const notes = approvalNotes[id] || null;
      const { error } = await supabase.from("purchase_orders").update({
        status,
        approved_by: user!.id,
        approved_by_name: fullName || user!.email,
        approval_notes: notes,
        updated_at: new Date().toISOString(),
      }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-purchase-orders"] });
      toast({ title: "Order Updated" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const pendingApprovals = orders.filter((o) => o.status === "pending_approval");
  const filteredOrders = orders.filter((o) => {
    if (filterScope !== "all" && o.scope !== filterScope) return false;
    if (filterStatus !== "all" && o.status !== filterStatus) return false;
    return true;
  });

  const totalSpend = orders.filter((o) => ["approved", "ordered", "delivered", "completed"].includes(o.status)).reduce((s, o) => s + Number(o.estimated_total || 0), 0);
  const lowStockItems = inventory.filter((i) => Number(i.quantity_on_hand) <= Number(i.minimum_quantity));

  const generateReport = () => {
    let report = "SUPPLY CHAIN REPORT\n";
    report += `Generated: ${format(new Date(), "PPpp")}\n`;
    report += `Generated by: ${fullName || user?.email}\n\n`;
    report += "=" .repeat(60) + "\nORDER SUMMARY\n" + "=".repeat(60) + "\n\n";
    report += `Total Orders: ${orders.length}\n`;
    report += `Total Approved Spend: $${totalSpend.toLocaleString()}\n`;
    report += `Pending Approvals: ${pendingApprovals.length}\n\n`;

    const byStatus: Record<string, number> = {};
    orders.forEach((o) => { byStatus[o.status] = (byStatus[o.status] || 0) + 1; });
    report += "By Status:\n";
    Object.entries(byStatus).forEach(([s, c]) => { report += `  ${s}: ${c}\n`; });

    report += "\n" + "=".repeat(60) + "\nALL ORDERS\n" + "=".repeat(60) + "\n\n";
    orders.forEach((o) => {
      report += `${o.order_number} | ${o.title}\n`;
      report += `  Type: ${o.order_type} | Scope: ${o.scope}${o.vessel_name ? ` (${o.vessel_name})` : ""}\n`;
      report += `  Status: ${o.status} | Priority: ${o.priority} | Est: $${Number(o.estimated_total || 0).toLocaleString()}\n`;
      report += `  Requested: ${o.requested_by_name} on ${format(new Date(o.created_at), "PPp")}\n`;
      if (o.approved_by_name) report += `  Approved/Rejected by: ${o.approved_by_name}\n`;
      if (o.approval_notes) report += `  Notes: ${o.approval_notes}\n`;
      report += "\n";
    });

    report += "=".repeat(60) + "\nLOW STOCK ALERTS\n" + "=".repeat(60) + "\n\n";
    lowStockItems.forEach((i) => {
      report += `${i.name} (${i.part_number || "N/A"}) — ${i.quantity_on_hand}/${i.minimum_quantity} ${i.unit} at ${i.location_type === "vessel" ? i.vessel_name : "Warehouse"}\n`;
    });

    const blob = new Blob([report], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `supply-chain-report-${format(new Date(), "yyyy-MM-dd")}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-display font-bold text-primary-foreground">Supply Chain Management</h1>
            <p className="text-sm text-primary-foreground/50 font-body mt-1">Oversee purchasing, approvals, and inventory</p>
          </div>
          <Button onClick={generateReport} variant="outline" className="gap-2"><Download className="w-4 h-4" />Export Report</Button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border"><CardContent className="p-4 flex items-center gap-3"><DollarSign className="w-8 h-8 text-teal" /><div><p className="text-xs text-muted-foreground">Approved Spend</p><p className="text-lg font-bold text-primary-foreground">${totalSpend.toLocaleString()}</p></div></CardContent></Card>
          <Card className="bg-card border-border"><CardContent className="p-4 flex items-center gap-3"><Package className="w-8 h-8 text-blue-400" /><div><p className="text-xs text-muted-foreground">Total Orders</p><p className="text-lg font-bold text-primary-foreground">{orders.length}</p></div></CardContent></Card>
          <Card className="bg-card border-border"><CardContent className="p-4 flex items-center gap-3"><AlertTriangle className="w-8 h-8 text-amber-400" /><div><p className="text-xs text-muted-foreground">Pending Approvals</p><p className="text-lg font-bold text-primary-foreground">{pendingApprovals.length}</p></div></CardContent></Card>
          <Card className="bg-card border-border"><CardContent className="p-4 flex items-center gap-3"><Warehouse className="w-8 h-8 text-destructive" /><div><p className="text-xs text-muted-foreground">Low Stock</p><p className="text-lg font-bold text-primary-foreground">{lowStockItems.length}</p></div></CardContent></Card>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-card border border-border">
            <TabsTrigger value="approvals" className="gap-2"><CheckCircle className="w-4 h-4" />Approvals ({pendingApprovals.length})</TabsTrigger>
            <TabsTrigger value="all-orders" className="gap-2"><ShoppingCart className="w-4 h-4" />All Orders</TabsTrigger>
            <TabsTrigger value="inventory" className="gap-2"><Warehouse className="w-4 h-4" />Inventory</TabsTrigger>
          </TabsList>

          {/* APPROVALS */}
          <TabsContent value="approvals" className="space-y-4 mt-4">
            {pendingApprovals.length === 0 ? (
              <div className="text-center py-12 text-primary-foreground/40">No orders pending approval.</div>
            ) : (
              pendingApprovals.map((o) => {
                const est = Number(o.estimated_total || 0);
                const tier = getApprovalTier(est, o.order_type as "material" | "service", o.scope as "corporate" | "vessel");
                const chain = getApprovalChain(tier, o.status, o.approved_by);
                return (
                <Card key={o.id} className="bg-card border-amber-500/30">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <code className="text-xs font-mono text-teal bg-teal/10 px-2 py-0.5 rounded">{o.order_number}</code>
                          <Badge variant="outline" className="text-[10px] font-mono border-teal/30 text-teal">{tier.id}</Badge>
                          <Badge variant="outline" className="text-primary-foreground/50 border-border">
                            {o.scope === "vessel" ? <><Ship className="w-3 h-3 mr-1" />{o.vessel_name}</> : <><Building2 className="w-3 h-3 mr-1" />Corporate</>}
                          </Badge>
                        </div>
                        <h3 className="text-sm font-semibold text-primary-foreground">{o.title}</h3>
                        <p className="text-xs text-muted-foreground mt-1">By {o.requested_by_name} • {format(new Date(o.created_at), "MMM d, yyyy")}{o.vendor_name ? ` • Vendor: ${o.vendor_name}` : ""}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-primary-foreground">${est.toLocaleString()}</p>
                        <p className="text-[10px] text-muted-foreground">{formatValueRange(tier)}</p>
                      </div>
                    </div>

                    {/* ACOC Chain of Custody */}
                    <div className="bg-background/50 rounded-lg p-3 border border-border">
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="w-3.5 h-3.5 text-teal" />
                        <span className="text-[10px] font-bold uppercase tracking-wider text-primary-foreground/60">Chain of Custody — {tier.id}</span>
                      </div>
                      <div className="flex items-center gap-3 flex-wrap">
                        {chain.map((step, i) => (
                          <div key={i} className="flex items-center gap-1.5">
                            <div className={`w-2 h-2 rounded-full ${step.status === "current" ? "bg-amber-400 animate-pulse" : "bg-muted"}`} />
                            <span className="text-[11px] text-muted-foreground">{step.label}</span>
                            {i < chain.length - 1 && <span className="text-muted-foreground/30">→</span>}
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center gap-1.5 mt-2">
                        <Gavel className="w-3 h-3 text-muted-foreground" />
                        <p className="text-[10px] text-muted-foreground">{tier.llcaNote}</p>
                      </div>
                    </div>

                    <div className="flex items-end gap-3">
                      <div className="flex-1">
                        <Label className="text-xs text-primary-foreground/50">Approval Notes</Label>
                        <Textarea value={approvalNotes[o.id] || ""} onChange={(e) => setApprovalNotes({ ...approvalNotes, [o.id]: e.target.value })} className="bg-background border-border text-primary-foreground text-xs h-16" placeholder="Optional notes..." />
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => updateStatus.mutate({ id: o.id, status: "approved" })} className="gap-1 bg-emerald-600 hover:bg-emerald-700"><CheckCircle className="w-4 h-4" />Approve</Button>
                        <Button onClick={() => updateStatus.mutate({ id: o.id, status: "rejected" })} variant="destructive" className="gap-1"><XCircle className="w-4 h-4" />Reject</Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                );
              })
            )}
          </TabsContent>

          {/* ALL ORDERS */}
          <TabsContent value="all-orders" className="space-y-4 mt-4">
            <div className="flex flex-wrap items-center gap-3">
              <Select value={filterScope} onValueChange={setFilterScope}>
                <SelectTrigger className="w-[140px] bg-card border-border text-primary-foreground"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Scopes</SelectItem>
                  <SelectItem value="corporate">Corporate</SelectItem>
                  <SelectItem value="vessel">Vessel</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[160px] bg-card border-border text-primary-foreground"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="pending_approval">Pending Approval</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="ordered">Ordered</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {isLoading ? (
              <div className="text-center py-12 text-primary-foreground/40">Loading...</div>
            ) : filteredOrders.length === 0 ? (
              <div className="text-center py-12 text-primary-foreground/40">No orders found.</div>
            ) : (
              filteredOrders.map((o) => {
                const tier = getApprovalTier(Number(o.estimated_total || 0), o.order_type as "material" | "service", o.scope as "corporate" | "vessel");
                return (
                <Card key={o.id} className="bg-card border-border cursor-pointer hover:border-teal/30 transition-colors" onClick={() => setDetailOrder(o)}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <code className="text-xs font-mono text-teal bg-teal/10 px-2 py-0.5 rounded">{o.order_number}</code>
                          <Badge className={statusColors[o.status] || ""}>{o.status.replace("_", " ")}</Badge>
                          <Badge variant="outline" className="text-[10px] font-mono border-teal/30 text-teal">{tier.id}</Badge>
                          <Badge variant="outline" className="text-primary-foreground/50 border-border text-xs">
                            {o.scope === "vessel" ? o.vessel_name : "Corporate"}
                          </Badge>
                        </div>
                        <h3 className="text-sm font-semibold text-primary-foreground">{o.title}</h3>
                        <p className="text-xs text-muted-foreground mt-1">By {o.requested_by_name} • {format(new Date(o.created_at), "MMM d, yyyy")}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-primary-foreground">${Number(o.estimated_total || 0).toLocaleString()}</p>
                        {o.status !== "draft" && o.status !== "pending_approval" && (
                          <Select value={o.status} onValueChange={(val) => { updateStatus.mutate({ id: o.id, status: val }); }}>
                            <SelectTrigger className="w-[130px] h-7 text-xs bg-background border-border text-primary-foreground mt-1" onClick={(e) => e.stopPropagation()}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="approved">Approved</SelectItem>
                              <SelectItem value="ordered">Ordered</SelectItem>
                              <SelectItem value="delivered">Delivered</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                              <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
                );
              })
            )}
          </TabsContent>

          {/* INVENTORY */}
          <TabsContent value="inventory" className="space-y-4 mt-4">
            {inventory.length === 0 ? (
              <div className="text-center py-12 text-primary-foreground/40">No inventory items.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {inventory.map((item) => {
                  const isLow = Number(item.quantity_on_hand) <= Number(item.minimum_quantity);
                  return (
                    <Card key={item.id} className={`bg-card border-border ${isLow ? "border-amber-500/50" : ""}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="text-sm font-semibold text-primary-foreground">{item.name}</h4>
                            {item.part_number && <p className="text-xs text-muted-foreground font-mono">{item.part_number}</p>}
                          </div>
                          {isLow && <AlertTriangle className="w-4 h-4 text-amber-400" />}
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <Badge variant="outline" className="text-xs border-border text-primary-foreground/60">{item.category}</Badge>
                          <Badge variant="outline" className="text-xs border-border text-primary-foreground/60">{item.location_type === "vessel" ? item.vessel_name : "Warehouse"}</Badge>
                        </div>
                        <div className="mt-3 flex items-center justify-between text-xs">
                          <span className={`font-bold ${isLow ? "text-amber-400" : "text-primary-foreground"}`}>{item.quantity_on_hand} {item.unit}</span>
                          <span className="text-muted-foreground">Min: {item.minimum_quantity} {item.unit}</span>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Order Detail Dialog */}
        <Dialog open={!!detailOrder} onOpenChange={(open) => !open && setDetailOrder(null)}>
          <DialogContent className="max-w-lg bg-card border-border">
            <DialogHeader><DialogTitle className="text-primary-foreground">Order Details</DialogTitle></DialogHeader>
            {detailOrder && (
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <code className="text-xs font-mono text-teal bg-teal/10 px-2 py-0.5 rounded">{detailOrder.order_number}</code>
                  <Badge className={statusColors[detailOrder.status]}>{detailOrder.status.replace("_", " ")}</Badge>
                </div>
                <h3 className="font-semibold text-primary-foreground">{detailOrder.title}</h3>
                {detailOrder.description && <p className="text-muted-foreground text-xs">{detailOrder.description}</p>}
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-muted-foreground">Type:</span> <span className="text-primary-foreground">{detailOrder.order_type}</span></div>
                  <div><span className="text-muted-foreground">Scope:</span> <span className="text-primary-foreground">{detailOrder.scope}{detailOrder.vessel_name ? ` (${detailOrder.vessel_name})` : ""}</span></div>
                  <div><span className="text-muted-foreground">Priority:</span> <span className="text-primary-foreground">{detailOrder.priority}</span></div>
                  <div><span className="text-muted-foreground">Vendor:</span> <span className="text-primary-foreground">{detailOrder.vendor_name || "N/A"}</span></div>
                  <div><span className="text-muted-foreground">Requested:</span> <span className="text-primary-foreground">{detailOrder.requested_by_name}</span></div>
                  <div><span className="text-muted-foreground">Est. Total:</span> <span className="text-primary-foreground font-bold">${Number(detailOrder.estimated_total || 0).toLocaleString()}</span></div>
                  {detailOrder.approved_by_name && <div><span className="text-muted-foreground">Reviewed by:</span> <span className="text-primary-foreground">{detailOrder.approved_by_name}</span></div>}
                  {detailOrder.budget_code && <div><span className="text-muted-foreground">Budget:</span> <span className="text-primary-foreground">{detailOrder.budget_code}</span></div>}
                </div>
                {detailOrder.approval_notes && <div className="bg-background p-2 rounded text-xs"><span className="text-muted-foreground">Approval Notes: </span>{detailOrder.approval_notes}</div>}
                {orderItems.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-primary-foreground mb-1">Line Items</p>
                    <div className="space-y-1">
                      {orderItems.map((li) => (
                        <div key={li.id} className="flex items-center justify-between text-xs bg-background p-2 rounded">
                          <div><span className="text-primary-foreground">{li.item_description}</span>{li.part_number && <span className="text-muted-foreground ml-2">({li.part_number})</span>}</div>
                          <span className="text-primary-foreground">{li.quantity} × ${Number(li.unit_price).toFixed(2)} = ${Number(li.total_price).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminSupplyChain;
